import React, { useEffect, useState } from "react";
import { Alert, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SectionPanelProps } from "./SectionTypes";
import { useClientConfigService } from "../../../services/clients/ClientConfigServiceProvider";
import { StaticTokensProvider } from "../../../core/theme/TokensProvider";
import { BookingPricingEditor } from "../../../components/bookings/pricing/BookingPricingEditor";
import { BookingPricingSettings, DEFAULT_BOOKING_PRICING } from "../../../model/client/ClientSettings";
import { getCurrencySymbol } from "../../../utils/formatters";

const { Text } = Typography;

// Fortius admin settings shell section for a club's hourly booking prices (FORT-288).
// Same form clubs use on their own Booking Pricing page (BookingPricingEditor), loaded
// and saved by client id. The shell renders outside the tenant theme, so the editor's
// primitives get the base tokens.
export const BookingPricingSectionPanel: React.FC<SectionPanelProps<unknown>> = () => {
    const service = useClientConfigService();
    const { clientId: clientIdParam } = useParams<{ clientId: string }>();
    const clientId = clientIdParam !== undefined ? Number(clientIdParam) : NaN;
    const [loaded, setLoaded] = useState<{
        pricing: BookingPricingSettings;
        facilityTypes: string[];
        currencySymbol: string;
    } | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(clientId)) {
            setLoadError("Invalid client id in URL.");
            return;
        }
        setLoadError(null);
        let cancelled = false;
        (async () => {
            try {
                const [config, facilityTypes] = await Promise.all([
                    service.getConfig(clientId),
                    service.listFacilityTypes(clientId),
                ]);
                if (cancelled) return;
                if (!config) {
                    setLoadError("Failed to load settings.");
                    return;
                }
                setLoaded({
                    pricing: config.settings.bookingPricing ?? DEFAULT_BOOKING_PRICING,
                    facilityTypes: (facilityTypes ?? []).map((t) => t.name),
                    currencySymbol: getCurrencySymbol(config.settings.locale?.currency ?? "GBP"),
                });
            } catch (err) {
                if (!cancelled) setLoadError(err instanceof Error ? err.message : "Failed to load settings.");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [service, clientId]);

    if (loadError) {
        return <Alert type="error" message={loadError} data-testid="settings-booking-pricing-load-error" />;
    }
    if (loaded === null) {
        return <Text type="secondary">Loading…</Text>;
    }

    return (
        <div data-testid="settings-booking-pricing">
            <StaticTokensProvider>
                <BookingPricingEditor
                    facilityTypes={loaded.facilityTypes}
                    pricing={loaded.pricing}
                    currencySymbol={loaded.currencySymbol}
                    onSave={(pricing) => service.updateBookingPricingForClient(clientId, pricing)}
                />
            </StaticTokensProvider>
        </div>
    );
};
